import React, { useState, useRef, useEffect } from 'react';
import { TeamDto, fetchTeamsForFinish, stopTeam } from '../../../api/apis';
import TeamSearchResults from './TeamSearchResults';

const TeamSearch: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<TeamDto[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const loadTeams = async (query: string = '') => {
    try {
      setIsLoading(true);
      setError(null);
      const results = await fetchTeamsForFinish(query);
      setSearchResults(results);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to search teams');
      setSearchResults([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Load all teams on component mount
  useEffect(() => {
    loadTeams();
  }, []);

  const handleSearch = async (query: string) => {
    if (!query.trim()) {
      loadTeams(); // Load all teams when search is cleared
      return;
    }
    loadTeams(query);
  };

  const handleStopTeam = async (teamId: string) => {
    try {
      setIsLoading(true);
      setError(null);
      await stopTeam(teamId);
      loadTeams(searchQuery); // Refresh the list after stopping a team
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to stop team');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div ref={containerRef} className="space-y-4">
      <div className="relative">
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            handleSearch(e.target.value);
          }}
          placeholder="Search for a team..."
          className="w-full p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        {isLoading && (
          <div className="absolute right-3 top-2.5 text-gray-500">
            Loading...
          </div>
        )}
      </div>

      {error && (
        <div className="text-red-600 text-sm">{error}</div>
      )}

      {searchResults.length > 0 && (
        <TeamSearchResults
          results={searchResults}
          onStopTeam={handleStopTeam}
          isLoading={isLoading}
        />
      )}
    </div>
  );
};

export default TeamSearch; 